import React, { useState } from 'react'
import { Box, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import SearchBox from '../../rsmCoreComponents/components/Search/SearchBox'
import { Styles } from '../../types'
import UserAssignmentModal from './UserAssignmentModal'
import CustomSuccessAlert from '../forms/Alert/CustomSuccessAlert/CustomSuccessAlert'
import CustomErrorAlert from '../forms/Alert/CustomErrorAlert/CustomErrorAlert'

const styles: Styles = {
  searchContainer: (theme) => ({
    width: '100%',
    [theme.breakpoints.up('desktop')]: {
      width: '28.125rem',
    },
    [theme.breakpoints.up('tablet')]: {
      width: '25rem',
      marginRight: '0.31rem',
    },
  }),
  filterChipsContainer: () => ({
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'column', md: 'row' },
    alignItems: { xs: 'flex-start', md: 'center' },
    justifyContent: 'flex-start',
    gap: '1.25rem',
    width: '100%',
  }),
  buttonContainer: {
    display: 'flex',
    justifyContent: { xs: 'flex-start', md: 'flex-end' },
    width: { xs: '100%', sm: 'auto', md: 'auto' },
    mt: { xs: 2, md: 0 },
    flex: { md: 1 },
  },
  addNewUserButton: {
    minHeight: '2.75rem', // WCAG touch target requirement
    paddingX: 3,
    fontWeight: 600,
    fontFamily: 'Prelo-Light',
  },
}

interface UserManagementSearchProps {
  setFilter: React.Dispatch<React.SetStateAction<string>>
}

/**
 * UserManagementSearch component provides a search input functionality for the User Management interface.
 *
 * Features:
 * - Debounced keyword search with 255-character limit
 * - WCAG 2.1 AA compliant accessibility features
 * - Responsive design with theme-aware breakpoints
 * - Screen reader support with proper ARIA labels
 * - Keyboard navigation support
 *
 * @param setFilter - Callback function to handle search value changes and trigger debounced search
 */
const UserManagementSearch = ({ setFilter }: UserManagementSearchProps) => {
  const { t } = useTranslation()
  const [isUserAssignmentModalOpen, setIsUserAssignmentModalOpen] =
    useState(false)
  const [isSaveSuccess, setIsSaveSuccess] = useState(false)
  const [isError, setIsError] = useState(false)

  return (
    <Box
      id="user-management-filter-chips-clear-all-focus-target"
      sx={styles.filterChipsContainer}>
      <Box
        sx={styles.searchContainer}
        role="search"
        aria-label={t('UserManagement.SearchRegion')}>
        <SearchBox
          setFilter={setFilter}
          maxSearchLength={255}
          placeholderText={t('UserManagement.SearchByKeyword')}
          testId="Txt_UserManagement_Search"
        />
      </Box>
      <Box sx={styles.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          data-testid="Btn_AddNewUser"
          sx={styles.addNewUserButton}
          onClick={() => setIsUserAssignmentModalOpen(true)}
          aria-label={t(
            'UserManagement.AddNewUserAriaLabel',
            'Add new myRSM user to the system',
          )}>
          {t('UserManagement.AddNewMyRSMUser', 'Add new myRSM user')}
        </Button>
      </Box>
      <UserAssignmentModal
        open={isUserAssignmentModalOpen}
        onClose={() => setIsUserAssignmentModalOpen(false)}
        onSuccess={() => {
          setIsSaveSuccess(true)
          setIsUserAssignmentModalOpen(false)
        }}
        onError={() => {
          setIsError(false)
        }}
      />
      <CustomSuccessAlert
        header={t('UserManagement.AddUser.SuccessHeader')}
        message={t('UserManagement.AddUser.SuccessMessage')}
        open={isSaveSuccess}
        close={() => setIsSaveSuccess(false)}
        testId="Alert_UserManagement_Success"
      />
      <CustomErrorAlert
        message={t('UserManagement.AddUser.ErrorMessage')}
        open={isError}
        close={() => setIsError(false)}
        testId="Alert_UserManagement_Error"
      />
    </Box>
  )
}

export default UserManagementSearch
